import React, { useState, useEffect, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDarkMode } from '@/contexts/DarkModeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useUXPreferences } from '@/contexts/UXPreferencesContext';
import { isClientUser } from '@/app/router/helpers';
import { Type, Rows3 } from 'lucide-react';
import { formatProviderFullName } from '../../../utils/providerDisplayName';

// Honorific / title prefixes that should be stripped when computing initials
// from a combined name string (e.g. "Dr Sarah Johnson" -> "Sarah Johnson").
const TITLE_PREFIXES = new Set([
  'dr', 'mr', 'mrs', 'ms', 'mx', 'miss', 'prof', 'professor', 'rev', 'sir', 'dame', 'fr', 'sr', 'jr',
]);

// Credential / degree suffixes that should be stripped when computing initials
// (e.g. "Autumn Breeze, CMA" -> "Autumn Breeze").  These appear after the
// last name and are not part of the person's initials.
const CREDENTIAL_SUFFIXES = new Set([
  'md', 'do', 'phd', 'psyd', 'edd', 'lcsw', 'lmsw', 'lsw', 'lpc', 'lpcc', 'lmft', 'lmhc',
  'ma', 'ms', 'msw', 'msn', 'mft', 'np', 'pmhnp', 'cma', 'rn', 'lcpc', 'lcmhc',
  'mft-a', 'bc', 'bcba', 'casac', 'cadc', 'casi', 'jd', 'esq',
  // Generational suffixes (Thomas Smith Jr. -> "TS")
  'jr', 'sr', 'ii', 'iii', 'iv', 'v',
]);

function stripTitle(token: string): string {
  // Remove all dots/commas so "Dr." -> "dr" and "Ph.D." -> "phd".  Clinical
  // credentials are frequently written with internal periods (M.D., Psy.D.,
  // L.C.S.W.) and those must still match CREDENTIAL_SUFFIXES.
  return token.replace(/[.,]/g, '').toLowerCase();
}

function nameTokens(fullName: string): string[] {
  const tokens = fullName.trim().split(/\s+/).filter(Boolean);
  // Drop leading honorific titles.
  while (tokens.length > 1 && TITLE_PREFIXES.has(stripTitle(tokens[0]))) {
    tokens.shift();
  }
  // Drop trailing credential suffixes (after a comma or as a bare token).
  while (tokens.length > 1) {
    const last = stripTitle(tokens[tokens.length - 1]);
    if (CREDENTIAL_SUFFIXES.has(last)) {
      tokens.pop();
    } else {
      break;
    }
  }
  return tokens;
}

function firstAlpha(token: string | undefined | null): string {
  if (!token) return '';
  const match = token.match(/[A-Za-z]/);
  return match ? match[0].toUpperCase() : '';
}

function UserProfile({ user }) {
  const [isOpen, setIsOpen] = useState(false);
  const [photoBroken, setPhotoBroken] = useState(false);
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { logout } = useAuth();
  const { textSize, cycleTextSize, density, toggleDensity } = useUXPreferences();
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();

  const photoUrl = typeof user?.photo_url === 'string' && user.photo_url.trim() ? user.photo_url : '';
  useEffect(() => {
    setPhotoBroken(false);
  }, [photoUrl]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle Escape key to close dropdown
  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    }
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);

  const handleLogout = async () => {
    await logout('/login');
  };

  const getUserInitials = () => {
    if (!user) return '?';

    // Prefer explicit first_name / last_name columns when available.  These
    // are the most reliable source because they skip honorifics ("Dr.") and
    // credential suffixes (", LCSW") that contaminate the combined ``name``
    // string and the ``preferred_name`` (which may be only a first name).
    const firstFromField = firstAlpha(user.first_name);
    const lastFromField = firstAlpha(user.last_name);
    if (firstFromField && lastFromField) {
      return firstFromField + lastFromField;
    }

    // Fall back to parsing the combined display name.  Strip leading titles
    // (Dr., Prof., etc.) and trailing credential suffixes (", LCSW") so
    // "Dr Sarah Johnson" -> "SJ" and "Autumn Breeze, CMA" -> "AB".
    const combined = [user.name, user.preferred_name]
      .filter((s): s is string => typeof s === 'string' && s.trim().length > 0);
    for (const candidate of combined) {
      const tokens = nameTokens(candidate);
      if (tokens.length >= 2) {
        const first = firstAlpha(tokens[0]);
        const last = firstAlpha(tokens[tokens.length - 1]);
        if (first && last) return first + last;
      }
    }

    // If we have a first_name or last_name but not both, pair it with the
    // other parsed-name token so we still produce a first+last style result.
    if (firstFromField || lastFromField) {
      for (const candidate of combined) {
        const tokens = nameTokens(candidate);
        if (tokens.length >= 2) {
          const first = firstFromField || firstAlpha(tokens[0]);
          const last = lastFromField || firstAlpha(tokens[tokens.length - 1]);
          if (first && last) return first + last;
        }
      }
    }

    // Single-token name (e.g. preferred_name = "Erin"): use first two
    // characters of that token as a last resort before falling back to email.
    for (const candidate of combined) {
      const tokens = nameTokens(candidate);
      if (tokens.length === 1 && tokens[0].length >= 2) {
        return tokens[0].substring(0, 2).toUpperCase();
      }
    }

    // Fall back to email-based initials
    if (!user.email) return '?';

    const localPart = user.email.split('@')[0];
    const rawParts = localPart.split(/[._-]/);
    const parts = rawParts.filter(Boolean); // Remove empty segments from split

    if (parts.length >= 2 && parts[0] && parts[1]) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    if (parts.length >= 1 && parts[0]) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    // Ultimate fallback: use raw localPart
    return localPart.substring(0, 2).toUpperCase();
  };

    return (
      <div className="relative" ref={dropdownRef}>
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-400 rounded-full"
          aria-expanded={isOpen}
          aria-haspopup="menu"
          aria-label="User menu"
          title="Your profile and settings"
        >
          <span className="sr-only">Open user menu</span>
        <div className={`h-8 w-8 rounded-full flex items-center justify-center overflow-hidden text-sm font-medium text-white ${isDarkMode ? 'bg-blue-600' : 'bg-blue-500'}`}>
          {photoUrl && !photoBroken ? (
            <img
              src={photoUrl}
              alt={formatProviderFullName(user)}
              className="h-full w-full object-cover"
              onError={() => setPhotoBroken(true)}
            />
          ) : (
            getUserInitials()
          )}
        </div>
      </button>

      {isOpen && (
        <div
          className={`absolute right-0 mt-2 w-56 rounded-md shadow-lg ${isDarkMode ? 'bg-gray-800 ring-gray-700' : 'bg-white ring-black ring-opacity-5'} ring-1 z-50 dark:bg-gray-800 dark:ring-gray-700`}
        >
          <div className={`py-3 px-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {formatProviderFullName(user)}
            </div>
            {user?.role && (
              <div className="text-xs text-blue-600 dark:text-blue-400 capitalize">
                {user.role.replace(/_/g, ' ')}
              </div>
            )}
            <div className="text-xs text-gray-500 truncate dark:text-gray-400">
              {user?.email || 'user@example.com'}
            </div>
          </div>
          
          <div className="py-1">
            <button
              onClick={toggleDarkMode}
              className={`flex items-center w-full px-4 py-2 text-sm ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
            >
              {isDarkMode ? (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"></path>
                  </svg>
                  Light Mode
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"></path>
                  </svg>
                  Dark Mode
                </>
              )}
            </button>
            
            <button
              onClick={cycleTextSize}
              className={`flex items-center justify-between w-full px-4 py-2 text-sm ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
            >
              <span className="flex items-center">
                <Type className="w-4 h-4 mr-2" />
                Text Size
              </span>
              <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                {textSize.charAt(0).toUpperCase() + textSize.slice(1)}
              </span>
            </button>
            
            <button
              onClick={toggleDensity}
              className={`flex items-center justify-between w-full px-4 py-2 text-sm ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
            >
              <span className="flex items-center">
                <Rows3 className="w-4 h-4 mr-2" />
                Density
              </span>
              <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                {density.charAt(0).toUpperCase() + density.slice(1)}
              </span>
            </button>
            
            {user?.demo_mode && (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/demo');
                  setIsOpen(false);
                }}
                className={`block px-4 py-2 text-sm ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                Switch Character
              </a>
            )}
            
            <div className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} my-1`}></div>
            
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate(isClientUser(user) ? '/client?tab=settings' : '/my-settings?tab=profile');
                setIsOpen(false);
              }}
              className={`block px-4 py-2 text-sm ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
            >
              My Profile
            </a>
            
            <div className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} my-1`}></div>
            
            <button
              type="button"
              onClick={handleLogout}
              className={`block w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(UserProfile);
